import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box } from '@mui/joy';
import { getAllApplicants } from '../../../apiconfig/api';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import ReactGA from "react-ga4";
import DeprecatedCandidatesList from './components/DeprecatedCandidatesList';
import PostJobPromotion from './components/PostJobPromotion';
import { useSession } from 'context/SessionContext';

const Candidates: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasSetDefaultRef = useRef(false);
  
  const zipcode = searchParams.get('zipcode') || '';
  const rolesParam = searchParams.get('roles') || '';
  const employersParam = searchParams.get('employers') || '';
  const employerPositionsParam = searchParams.get('employerPositions') || '';
  const roles = useMemo(() => 
    rolesParam ? rolesParam.split(',').map(p => p.trim()).filter(Boolean) : [], 
    [rolesParam]
  );
  const employerPositions = useMemo(() => 
    employerPositionsParam ? employerPositionsParam.split(',').map(p => p.trim()).filter(Boolean) : [], 
    [employerPositionsParam]
  );
  const employers = useMemo(() => 
    employersParam ? employersParam.split(',').map(p => p.trim()).filter(Boolean) : [], 
    [employersParam]
  );
  const { sessionEmployerId, isLoggedIn } = useSession();

  // Set default employer filter on initial load if no employers param exists
  useEffect(() => {
    if (sessionEmployerId && !employersParam && !hasSetDefaultRef.current) {
      hasSetDefaultRef.current = true;
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('employers', sessionEmployerId);
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [sessionEmployerId, employersParam, searchParams, setSearchParams]);

  const fetchCandidates = useCallback(async (page: number, per_page: number) => {
    return await getAllApplicants({
      zipcode,
      globalPositionIds: roles.length > 0 ? roles : undefined,
      submittedToEmployerIds: employers.length > 0 ? employers : undefined,
      submittedToEmployerPositionIds: employerPositions.length > 0 ? employerPositions : undefined,
      currentEmployerId: sessionEmployerId || undefined,
      page,
      per_page,
      isGloballyVisible: true,
      forcePrivate: true
    });
  }, [zipcode, roles, employerPositions, employers, sessionEmployerId]);

  // Infinite scroll hook for people
  const {
    data: candidates,
    loading: candidatesLoading,
    error,
    hasMore,
    loadMore,
    total: totalCandidates
  } = useInfiniteScroll({
    fetchFunction: fetchCandidates,
    perPage: 5
  });

  useEffect(() => {
    if (zipcode && candidates.length > 0) {
      ReactGA.event({
        category: "Hiring",
        action: "View People List",
        label: `ZipCode: ${zipcode}${roles.length > 0 ? `, Position: ${roles.join(', ')}` : ''}`
      });
    }
  }, [zipcode, roles, candidates.length]);

  return (
    <Box sx={{ 
      minHeight: "100vh", 
      bgcolor: "#f4f4f4",
      display: "flex",
      flexDirection: "column"
    }}>
      <PostJobPromotion />
      {isLoggedIn && (
        <DeprecatedCandidatesList 
          candidates={candidates} 
          isEmployerLoggedIn={!!sessionEmployerId}
          loading={candidatesLoading}
          hasMore={hasMore}
          onLoadMore={loadMore}
          totalCandidates={totalCandidates}
          error={error}
        />
      )}
    </Box>
  );
};

export default Candidates;
